import type { Permission } from './permissions';

/** Resource types supported by the Resource Hub layout pattern. */
export type ResourceType =
  | 'salesforce_org'
  | 'telephony_connection'
  | 'phone_number'
  | 'agent';

export type HubTabDefinition = {
  id: string;
  label: string;
  path: string;
  requiredPermissions?: Permission[];
};

export type ResourceHubDefinition = {
  resourceType: ResourceType;
  /** Build base path for a resource instance */
  basePath: (id: string) => string;
  defaultTab: string;
  tabs: HubTabDefinition[];
  breadcrumbParent: { label: string; href: string };
};

export const SALESFORCE_ORG_HUB: ResourceHubDefinition = {
  resourceType: 'salesforce_org',
  basePath: (id) => `/salesforce/${id}`,
  defaultTab: 'overview',
  breadcrumbParent: { label: 'Salesforce', href: '/salesforce' },
  tabs: [
    { id: 'overview', label: 'Overview', path: 'overview', requiredPermissions: ['salesforce:view'] },
    {
      id: 'authentication',
      label: 'Authentication',
      path: 'authentication',
      requiredPermissions: ['salesforce:edit'],
    },
    { id: 'routing', label: 'Routing', path: 'routing', requiredPermissions: ['salesforce:view'] },
    {
      id: 'telephony',
      label: 'Telephony',
      path: 'telephony',
      requiredPermissions: ['salesforce:view'],
    },
    {
      id: 'agents',
      label: 'Salesforce Agents',
      path: 'agents',
      requiredPermissions: ['agents:view'],
    },
  ],
};

export const TELEPHONY_CONNECTION_HUB: ResourceHubDefinition = {
  resourceType: 'telephony_connection',
  basePath: (id) => `/telephony/${id}`,
  defaultTab: 'overview',
  breadcrumbParent: { label: 'Telephony', href: '/telephony' },
  tabs: [
    { id: 'overview', label: 'Overview', path: 'overview', requiredPermissions: ['telephony:view'] },
    { id: 'config', label: 'Configuration', path: 'config', requiredPermissions: ['telephony:edit'] },
    {
      id: 'numbers',
      label: 'Phone Numbers',
      path: 'numbers',
      requiredPermissions: ['telephony:view'],
    },
    { id: 'webhooks', label: 'Webhooks', path: 'webhooks', requiredPermissions: ['telephony:view'] },
  ],
};

export const RESOURCE_HUB_REGISTRY: Record<ResourceType, ResourceHubDefinition> = {
  salesforce_org: SALESFORCE_ORG_HUB,
  telephony_connection: TELEPHONY_CONNECTION_HUB,
  phone_number: {
    resourceType: 'phone_number',
    basePath: (id) => `/telephony/phone-numbers/${id}`,
    defaultTab: 'overview',
    breadcrumbParent: { label: 'Phone Numbers', href: '/telephony/phone-numbers' },
    tabs: [
      { id: 'overview', label: 'Overview', path: 'overview', requiredPermissions: ['telephony:view'] },
    ],
  },
  /** Portal agents use /users; Salesforce Agents live under the Salesforce Org hub. */
  agent: {
    resourceType: 'agent',
    basePath: (id) => `/users/${id}`,
    defaultTab: 'overview',
    breadcrumbParent: { label: 'Agents', href: '/users' },
    tabs: [
      { id: 'overview', label: 'Overview', path: 'overview', requiredPermissions: ['users:view'] },
    ],
  },
};

export function getResourceHub(type: ResourceType): ResourceHubDefinition {
  return RESOURCE_HUB_REGISTRY[type];
}

export function getHubTabPath(hub: ResourceHubDefinition, resourceId: string, tabId: string): string {
  const tab = hub.tabs.find((t) => t.id === tabId);
  if (!tab) {
    return `${hub.basePath(resourceId)}/${hub.defaultTab}`;
  }
  return `${hub.basePath(resourceId)}/${tab.path}`;
}
